import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  Input, NgZone,
  OnChanges, Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {SubscribedComponent} from "../../subscribed.component";
import * as d3 from 'd3';
import {BaseType, ScaleLinear, Series} from 'd3';
import moment from 'moment';
import {TableType} from "../../interfaces/paged";
import {ClientService} from "../../shared/client.service";
import {ProjectService} from "../../shared/project.service";
import {UsersService} from "../../shared/users.service";
import {Globals} from "../../shared/Globals";
import {User, UserStatus} from "../../interfaces/user";
import {Project, ProjectStatus} from "../../interfaces/project";
import {Client, ClientStatus} from "../../interfaces/client";
import {ProjectComponent} from "../../project/project.component";
import {UserComponent} from "../../user/user.component";
import {PopupDialogComponent} from "../../dialogs/popup-dialog/popup-dialog.component";
import {NbDialogService} from "@nebular/theme";
import {DirtyDeactivateGuard} from "../../shared/dirty-deactivate-guard.service";
import {ClientComponent} from "../../clients/client.component";
import {SettingsService} from "../../shared/settings.service";
import {AuthService} from "../../shared/auth.service";
import DurationConstructor = moment.unitOfTime.DurationConstructor;
import {ProjectAssignmentStatus} from "../../interfaces/memberAssignment";

export enum TimelineType{
  Day = 'day',
  Week = 'week',
  Month = 'month',
  Quarter = 'quarter',
  Year = 'year'
}

interface CellBox{
  id: number,
  scheduled: number,
  status?: UserStatus|ProjectStatus|ClientStatus,
  cumulative: number,
  cell?: Cell
}

interface Cell{
  from: Date|string,
  to: Date|string,
  workingdays: number,
  holidays: number,
  scheduled: number,
  duration: number,
  items: CellBox[],
  capacity: {hours:number, off_hours:number, to: Date|string, from: Date|string},

  row?: Row
}

interface Segment{
  capacity: {
    date: Date|string,
    hours: number,
    scheduled?: number
  }[],
  items: {Client_id: number
    Project_color: string,
    Project_end_on: Date|string,
    Project_id: number,
    Project_name: string,
    Project_start_on: Date|string,
    Project_status: number,
    User_id: number
    User_name: string,
    User_logo: string,
    Client_name: string,
    Client_logo: string,
    dedication: number
    end_on: Date|string,
    id: number
    start_on: Date|string}[];
}

interface Row{
  id: number
  buckets: Cell[],
  max_score: number,
  max: number,
  status: ProjectAssignmentStatus,
  user?: User,
  project?: Project,
  client?: Client,

  keys?: {[key: string]: any},
  scale?: ScaleLinear<number, number>
}

@Component({
    selector: 'bst-timeline-chart',
    templateUrl: './timeline-chart.component.html',
    styleUrls: ['./timeline-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class TimelineChartComponent extends SubscribedComponent implements AfterViewInit, OnChanges{

  private page: number = 1;

  TableType = TableType;

  @Input() sidebar: boolean = true;
  @Input() tabletype: TableType = TableType.Users;
  @Input() type: TimelineType = TimelineType.Day;
  @Input() showcapacity: boolean = this.settings.userSetting('timeline_cap', false);
  @Input() showtracked: boolean = this.settings.userSetting('timeline_tra', false);
  @Input() filter;
  @Input() bootstrap: boolean = true;
  @Input() center: Date = new Date();

  public count: number;
  public open: Row;

  readonly size: number = 40;
  readonly buckets: number = 50; // Should be even
  readonly cellwidth: number = 140;
  readonly cellheight: number = 85;
  readonly strokewidth: number = 1;
  readonly dayheight: number = 15;
  readonly cellmargin = {
    top: 20,
    bottom: 0,
    right: 0,
    left: 1
  };

  private animate: boolean = false;
  private booted: boolean = false;
  private horizontalappending: boolean = false;
  private verticalappending: boolean = false;
  private curdates: Date[];
  public curdata: Row[];
  public segments:Segment;
  private weekends = [0, 6];

  private asses = null;

  @ViewChild('frame', {static: true}) frame: ElementRef;
  @ViewChild('container', {static: true}) container: ElementRef;
  @ViewChild('h1', {static: true}) h1: ElementRef;
  @ViewChild('h0', {static: true}) h0: ElementRef;
  @ViewChild('body', {static: true}) body: ElementRef;
  @ViewChild('side', {static: true}) side: ElementRef;

  constructor(private clientService: ClientService,
              private projectService: ProjectService,
              private userService: UsersService,
              private dirtyDeactivate: DirtyDeactivateGuard,
              private detector: ChangeDetectorRef,
              private settings: SettingsService,
              private auth: AuthService,
              private zone: NgZone,
              private dialogService: NbDialogService) {
    super();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if(this.booted){
      if(changes.tabletype || changes.type || changes.showcapacity || changes.showtracked) {
        await this.refresh(false, null, changes.tabletype !== null && typeof changes.tabletype !== 'undefined');
      }

      if(changes.type) {
        this.updateSettings('timeline_type', changes.type.currentValue?.toString() || TimelineType.Day);
      }
      if(changes.showcapacity){
        this.updateSettings('timeline_cap', <boolean>(changes.showcapacity.currentValue) || false);
      }
      if(changes.showtracked){
        this.updateSettings('timeline_tra', <boolean>(changes.showtracked.currentValue) || false);
      }
    }
  }

  updateSettings(key: string, value: any){
    this.settings.putUserSetting(key, value);
  }

  async ngAfterViewInit(): Promise<void>{
    if(this.bootstrap) {
      await this.refresh(true);
    }
  }

  async refresh(scroll: boolean = false, center: Date = null, clear = true){
    if(center){
      this.center = center;
    }

    if(clear){
      this.clearOpenElement();
      this.open = null;
      this.page = 1;
    }
    this.animate = true;
    await this.redraw();
    this.animate = false;
    if(clear){
      this.f.scrollTop = 0;
    }
    if(scroll) {
      this.centerVerticalScroll();
    }
  }

  centerVerticalScroll(offset: number = 0){
    if(this.f && this.container) {
      this.f.scrollLeft = this.container.nativeElement.getBoundingClientRect().width/2
        - this.f.getBoundingClientRect().width/2 + offset
    }
  }

  centerHorizontalScroll(offset: number = 0){
    if(this.f && this.container) {
      this.f.scrollTop = this.container.nativeElement.getBoundingClientRect().height/2
        - this.f.getBoundingClientRect().height/2 + offset
    }
  }

  centerDate(forunit: TimelineType = this.type){
    return moment(this.center).startOf(<DurationConstructor>forunit.toString());
  }

  dates(type: TimelineType = this.type, start = null, end = null): Date[]{
    const unit = <DurationConstructor>type.toString();
    start = start ? moment(start) : moment(this.centerDate(type).subtract(this.buckets/2, unit));
    end = end ? moment(end) : moment(start).add(this.buckets, unit);

    const dates = [];
    for(let cur = start; cur<=end; cur = cur.add(1, unit)){
      dates.push(cur.toDate());
    }
    return dates;
  }

  superUnitForUnit(type: TimelineType = this.type){
    switch (type){
      case TimelineType.Day:
        return TimelineType.Week;
      case TimelineType.Week:
        return TimelineType.Month;
      case TimelineType.Month:
        return TimelineType.Quarter;
      default:
        return TimelineType.Year
    }
  }

  async openRow(item: Row){
    this.clearOpenElement();
    if(this.open && this.open.id === item.id){
      this.open = null;
    }
    else {
      this.open = item;
    }
    await this.redraw();
    this.calcopen();
  }

  private clearOpenElement(){
    this.asses = null;
    d3.select(this.body.nativeElement).selectAll('.tml-row .tml-gantt').remove();
  }

  async redraw(){
    await this.draw().catch(e => {
      console.error(e);
      // A failed/aborted draw (e.g. the timeline endpoint 500s) must still clear
      // the loading flag — draw() sets loading.next(true) up front but throws
      // before the loading.next(false) at the end, leaving the chart stuck behind
      // its grey loading frame forever. Fall back to an empty dataset on first
      // load so the designed empty state renders instead of a bare frame; a later
      // successful refresh repopulates curdata.
      if(!this.curdata){ this.curdata = []; }
      this.loading.next(false);
    });
    this.detector.detectChanges();
  }

  get fullEdit(){
    return (this.auth.isStaffer || this.auth.isAdmin);
  }

  private async draw(){
    this.loading.next(true);
    const fullEdit = this.fullEdit;

    const height = (this.cellheight - this.cellmargin.top);
    const h1dates = this.dates();
    this.curdates = h1dates;
    const extent = d3.extent(h1dates);
    const superunit = this.superUnitForUnit();
    const h0dates = this.dates(superunit, moment(extent[0]).startOf(superunit).toDate(), extent[1]);
    const width = h1dates.length * this.cellwidth;

    const bucketDates = this.bucketDates(h1dates);
    let result;
    switch (this.tabletype){
      case TableType.Clients:
        result = await this.clientService.findTimeline(bucketDates, this.page, this.size, this.filter);
        break;
      case TableType.Projects:
        result = await this.projectService.findTimeline(bucketDates, this.page, this.size, this.filter);
        break;
      default:
        result = await this.userService.findTimeline(bucketDates, this.page, this.size, this.filter);
        break;
    }

    this.segments = null;
    if(this.open && result && result.data){
      const rowlink = result.data.find((o) => o.id === this.open.id);
      if(rowlink){
        const start = Globals.toISODate(extent[0]);
        const end = Globals.toISODate(extent[1]);
        switch (this.tabletype){
          case TableType.Clients:
            this.segments = await this.clientService.segments(rowlink.id, start, end);
            break;
          case TableType.Projects:
            this.segments = await this.projectService.segments(rowlink.id, start, end);
            break;
          default:
            this.segments = await this.userService.segments(rowlink.id, start, end);
            break;
        }
      }
    }

    d3.select(this.container.nativeElement)
      .style('width', (width + 200) + 'px');

    const scale = d3.scaleTime()
      .domain(extent)
      .range([0, width])
      .clamp(true)

    const drawHeaderDates = (under: ElementRef, unit: TimelineType, dates: Date[]) => {
      const cells = d3.select(under.nativeElement).selectAll<HTMLDivElement, BaseType>('.tml-header-cell').data(dates);

      // removes
      cells.exit().remove();

      //enters
      const points = cells.enter().append('div').classed('tml-cell tml-header-cell', true)
      points.append('span');

      // merges
      points.merge(cells).style('width', (datum, i) => {
          const next = dates[i + 1];
          let w;
          const where = scale(datum);
          if (next) {
            const nextwhere = scale(next);
            w = nextwhere - where;
          } else {
            w = width - where;
          }
          return w + 'px';
        })
        .classed('tml-weekend', (datum) => {
          return unit === TimelineType.Day && this.weekends.includes(datum.getDay());
        })
        .style('left', (datum, i) => {
          return scale(datum) + 'px';
        }).select('span').html((datum, i) => {
          return i < dates.length-1 ? this.displayFor(datum, unit) : null;
        });

      return points;
    };

    drawHeaderDates(this.h0, superunit, h0dates);
    drawHeaderDates(this.h1, this.type, h1dates);


    // Stacked Area Chart Data
    if(result && this.body && this.side){
      this.curdata = result.data;
      this.count = result.total;
      if((!this.curdata || !this.curdata.length) && this.page > 1){
        // Double check to make sure we are okay
        this.page = 1;
        return await this.draw();
      }

      const rows = d3.select(this.body.nativeElement).selectAll<HTMLDivElement, BaseType>('.tml-row')
        .data(this.curdata)
        .join((enter) => {
            const r = enter.append('div').classed('tml-row', true).append('div').classed( 'tml-overview', true)
            r.append('div').classed('tml-cells', true);
            const s = r.append('svg');
            s.append('g').classed('tml-paths', true);
            const g = s.append('g').classed('tml-capacity', true);
            g.append('clipPath').append('path');
            g.append('path').classed('tml-cap', true);
            g.append('path').classed('tml-line', true);
            g.append('path').classed('tml-dur', true);
            return r;
          }, (update) => {
            return update;
          },
          (exit) => {
            return exit.remove();
          })
        .attr('data-id', (d) => {
          return d.id;
        });
        // .on('mouseover', (e: MouseEvent) => {
        //   d3.select(<any>e.currentTarget).selectAll('.tml-line')
        //     .transition()
        //     .duration(100)
        //     .attr('stroke-opacity', 1)
        // }).on('mouseout', (e: MouseEvent) => {
        //   d3.select(<any>e.currentTarget).selectAll('.tml-line')
        //     .transition()
        //     .duration(100)
        //     .attr('stroke-opacity', 0);
        // });

      // Area generator
      const area = d3.area()
        .curve(d3.curveMonotoneX)
        .x((datum: any) => {
          let d = datum;
          if(datum.data){
            d = datum.data;
          }

          const t = scale(<Date>(d.to));
          const f = scale(<Date>(d.from));
          return f + (t - f)/2;
        })
        .y0((datum: any) => {
          let d = datum;
          if(datum.data){
            d = datum.data;
          }

          const scale = d.row.scale;
          return scale(datum[0]);
        })
        .y1((datum: any) => {
          let d = datum;
          if(datum.data){
            d = datum.data;
          }

          const scale = d.row.scale;
          return scale(datum[1]);
        });

      // Paths
      rows.select('svg > g.tml-paths').selectAll<SVGPathElement, Series<{[key: string]: number}, string>>('.tml-bar')
        .data((row) => {
          row.scale = d3.scaleLinear().domain([0, row.max]).range([this.cellheight, this.cellmargin.top]);

          const keys = {};
          const stackdata = row.buckets.map((b) => {
            const items = {};
            for(let i in b.items){
              const id = b.items[i].id;
              keys[id] = {
                status: b.items[i].status ?? 1
              };
              if(!items[id]){
                items[id] = 0;
              }

              items[id] += b.items[i].scheduled;
            }

            return {
              cell: b,
              from: Globals.fromDateString(b.from),
              to: Globals.fromDateString(b.to),
              items,
              row
            };
          });

          row.keys = keys;

          return d3.stack()
            .keys(Object.keys(keys).sort((a, b) => {
              return keys[a].status - keys[b].status;
            }))
            .value((d, key) => {
              return d.items[key] || 0;
            })
            .order(d3.stackOrderNone)
            .offset(d3.stackOffsetNone)(<any>stackdata);
        })
        .join((enter) => {
          return enter.append('path').classed('tml-bar', true);
        }, (update) => {
          return update;
        }, (exit) => {
          return exit.remove();
        })
        .on('click', (event, datum) => {
          if(datum[0] && (<any>(datum[0]['data'])?.row)?.id === this.auth.user.getValue().id || fullEdit) {
            this.preview(parseInt(datum.key));
          }
        })
        .classed('clickable', (val, i) => {
          if(val[0]) {
            return (<any>(val[0]['data'])?.row)?.id === this.auth.user.getValue().id || fullEdit;
          }
          return false;
        })
        .classed('fill-hatched', (val, i) => {
          const assignmentstatus = (<any>(val[0]['data']?.row)).status;
          const info = (<any>(val[0]['data']?.row))?.keys[val.key] || null;
          const status = info?.status;
          if(status === UserStatus.PENDING
            || status === ProjectStatus.PENDING
            || assignmentstatus === ProjectAssignmentStatus.PENDING){
            return true;
          }
          return false;
        })
        .attr('d', <any>area);

      const converter = (row) => {
        return row.buckets.map((b) => {
          b.from = Globals.fromDateString(b.from);
          b.to = Globals.fromDateString(b.to);
          b.row = row;
          return b;
        });
      };

      const xline = (to: Date, from: Date) => {
        const t = scale(to);
        const f = scale(from);
        return f + (t - f)/2;
      };

      const gtml = rows.select<SVGGElement>('svg > g.tml-capacity')
        .style('transform', (d) => {
          return d.max ? null : 'translateY(31.5px)';
        })
        .datum(converter);

      // Duration lines
      const dur = d3.line()
        .curve(d3.curveMonotoneX)
        .x((datum) => xline(<Date>(<any>datum).to, <Date>(<any>datum).from))
        .y((datum) => {
          const yscale = (<any>datum).row.scale;
          return Math.min(yscale((<any>datum).duration || 0), this.cellheight-1);
        });

      const durline = gtml.select<SVGPathElement>('path.tml-dur')
        .attr('stroke-width', this.strokewidth)
        .attr('stroke-opacity', this.showtracked ? 1 : 0)
        .attr('d', <any>dur);

      // Capacity lines
      if(this.tabletype === TableType.Users) {

        // Capacity line
        const line = d3.line()
          .curve(d3.curveMonotoneX)
          .x((datum) => xline(<Date>(<any>datum).to, <Date>(<any>datum).from))
          .y((datum) => {
            const cap = (<any>datum).capacity;
            const yscale = (<any>datum).row.scale;
            if(cap) {
              return Math.min(yscale(cap.hours || 0), this.cellheight-1);
            }
            return yscale(0);
          });

        // Red Area
        const area2 = d3.area()
          .curve(d3.curveMonotoneX)
          .x((datum) => xline(<Date>(<any>datum).to, <Date>(<any>datum).from))
          .y1(0)
          .y0((datum) => {
            const cap = (<any>datum).capacity;
            const yscale = (<any>datum).row.scale;
            if(cap) {
              return yscale(cap.hours || 0);
            }
            return yscale(0);
          });

        const area3 = d3.area()
          .curve(d3.curveMonotoneX)
          .x((datum: any) => xline(<Date>(<any>datum).to, <Date>(<any>datum).from))
          .y1((d: any) => {
            const scale = d.row.scale;
            return scale(0);
          })
          .y0((d: any) => {
            const scale = d.row.scale;
            return scale(d.scheduled || 0);
          });

        gtml.select<SVGClipPathElement>('clipPath')
          .attr('id', (d) => {
            return 'clip-' + d[0].row.id;
          }).select<SVGPathElement>('path')
          .attr('d', <any>area3);


        const lines2 = gtml.select<SVGPathElement>('path.tml-cap')
          .attr('clip-path', (d) => {
            return 'url(#clip-' + d[0].row.id + ')';
          })
          .attr('d', <any>area2);

        const lines = gtml.select<SVGPathElement>('path.tml-line')
          .attr('stroke-width', this.strokewidth)
          .attr('stroke-opacity', this.showcapacity ? 1 : 0)
          .attr('d', <any>line);
      }
      else{
        gtml.selectAll('path:not(.tml-dur)').attr('d', 'M0,0');
      }

      // Cells
      const cells = rows.select('.tml-cells').selectAll<HTMLDivElement, BaseType>('.tml-cell').data((row) => {
        return row.buckets.map((b) => {
          b.from = Globals.fromDateString(b.from);
          b.to = Globals.fromDateString(b.to);
          b.row = row;
          return b;
        });
      }).join((enter) => {
          const c = enter.append('div').classed('tml-cell', true);
          const h = c.append('span').classed('tml-bar-header', true);
          h.append('span').classed('tml-bar-header-dur', true);
          h.append('span').classed('tml-bar-header-sch', true);
          return c;
        }, (update) => {
          return update;
        }, (exit) => {
          return exit.remove();
        }).style('width', (datum, i) => {
        return (scale(<Date>datum.to) - scale(<Date>datum.from)) + 'px';
      }).style('left', (datum, i) => {
        return scale(<Date>(datum.from)) + 'px';
      })

      const tcells = cells.select('.tml-bar-header').datum((datum) => {
        const txt = {dur: null, sch: null, d: datum};
        if(datum.duration){
          const hours = Math.floor(datum.duration);
          const minutes = Math.floor(60 * (datum.duration - hours));
          txt.dur = (hours ? hours + 'h' : '') + (this.type === TimelineType.Day && minutes ? ' ' + minutes + 'm' : '');
        }
        if(datum.scheduled) {
          const hours = Math.floor(datum.scheduled);
          const minutes = Math.floor(60 * (datum.scheduled - hours));
          txt.sch = (hours || this.type === TimelineType.Day ? hours : '< 1') + 'h' + (this.type === TimelineType.Day && minutes ? ' ' + minutes + 'm' : '');
        }
        return txt;
      });

      tcells.select('.tml-bar-header-dur')
        .style('display', this.showtracked ? 'inline' : 'none')
        .text(datum => datum.dur);
      tcells.select('.tml-bar-header-sch')
        .classed('over', (datum) => datum.d.scheduled && datum.d.scheduled > datum.d.capacity?.hours)
        .text(datum => datum.sch);
    }

    // Draw open if needed
    this.clearOpenElement();
    if(this.segments){
      const stuff = d3.select(this.body.nativeElement)
        .select('.tml-row[data-id=\''+this.open.id+'\']');
      if(!stuff.empty()){
        const svg = stuff.append('div').classed('tml-gantt', true).append('svg');
        const box = svg.node().getBoundingClientRect();

        const oscale = d3.scaleBand(this.segments.items.map(o => {
          o.start_on = moment(o.start_on).toDate();
          o.start_on.setHours(0, 0, 0, 0);
          o.end_on = moment(o.end_on).toDate();
          o.end_on.setHours(0, 0, 0, 0);
          o.Project_start_on = moment(o.Project_start_on).toDate();
          o.Project_start_on.setHours(0, 0, 0, 0);
          o.Project_end_on = moment(o.Project_end_on).toDate();
          o.Project_end_on.setHours(0, 0, 0, 0);
          return o.id;
        }), [this.dayheight + 2, box.height]).padding(0.01);

        const capmap = {};
        this.segments.capacity.forEach((c) => {
          capmap[<string>(c.date)] = c;
          c.date = moment(c.date).toDate();
        });

        const cs = svg.append('g').selectAll('rect.tml-cap-day').data(this.segments.capacity);
        cs.join((enter) => {
            return enter.append('rect').classed('tml-cap-day', true);
          }, (update) => {
            return update;
          }, (exit) => {
            return exit.remove();
          })
          .attr('x', (d) => {
            return scale(<Date>(d.date));
          })
          .attr('y', 0)
          .attr('height', this.dayheight)
          .attr('width', (d, i, nodes) => {
            const next = d3.selectAll(nodes).data()[i+1];
            if(next){
              return scale(<Date>((<any>next).date)) - scale(<Date>(d.date));
            }
            return 0;
          })
          .attr('fill', (d) => {
            if(d.hours && d.scheduled > d.hours){
              return d3.interpolateOrRd(0.85);
            }
            else if(d.scheduled && d.hours) {
              return d3.interpolateBlues((d.scheduled / d.hours) * 0.85);
            }
            else if(d.scheduled){
              return d3.interpolateReds(0.9);
            }
            return 'white';
          });

        const barheight = Math.min(oscale.bandwidth(), 25);
        this.asses = svg.append('g').selectAll('g.tml-ass-cell').data(this.segments.items)
          .join((enter) => {
            const e = enter.append('g').classed('tml-ass-cell', true);
              e.append('rect');
              const g = e.append('g');
              g.append('text');
              g.append('image')
                .attr('x', 0)
                .attr('y', 1)
                .attr('height', barheight - 2)
              g.append('circle')
                .attr('cx', barheight / 2)
                .attr('cy', barheight / 2)
                .attr('r', barheight / 2 * .85);
              return e;
          }, (update) => {
            return update;
          }, (exit) => {
            return exit.remove();
          }).attr('transform', (d) => {
            return 'translate(' + scale(<Date>d.start_on) + ', ' + oscale(d.id) + ')';
          }).on('click', (event, d) => {
            let id = d.Project_id;
            if(this.tabletype === TableType.Projects){
              id = d.User_id;
            }
            this.preview(id);
          });

        this.asses.select('rect').attr('height', barheight)
          .attr('width', (d) => {
            return scale(<Date>d.end_on) - scale(<Date>d.start_on);
          })
          .classed('fill-hatched', (d) => d.Project_status === ProjectStatus.PENDING || d.status === ProjectAssignmentStatus.PENDING)
          .attr("rx", 1)
          .attr("ry", 1);

        this.asses.select('g > circle')
          .attr('fill', (d) => {
            return this.tabletype !== TableType.Projects ? d.Project_color : 'transparent'
          })
          .attr('stroke', '#ccc')
          .attr('stroke-width', (d) => {
            return this.tabletype !== TableType.Projects ? 0.5 : 0;
          });

        this.asses.select('g > image')
          .attr('href', (d) => {
            if(this.tabletype === TableType.Projects && d.User_logo){
              return Globals.logo(d.User_logo);
            }
            return null;
          });

        this.asses.select('g > text')
          .text((d) => {
            let name = d.Project_name;
            if(this.tabletype === TableType.Projects){
              name = d.User_name;
            }
            return name;
          })
          .attr('fill', (d) => {
            return '#000';
            // return d3.hsl(d.Project_color).l > 0.5 ? "#000" : "#fff";
          })
          .attr("dy", ".35em")
          .attr('dx', barheight + 4)
          .attr("x", 0)
          .attr("y", barheight / 2);


        // Marker
        const textdate = svg.append('text').classed('date', true).attr('opacity', 0);
        const textcap = svg.append('text').classed('cap', true).attr('opacity', 0);
        const marker = svg.append('line')
          .attr('stroke-opacity', 0)
          .classed('tml-marker', true);

        svg.on('mousemove', (e) => {
          const coords = d3.pointer(e);
          const date = this.round(scale.invert(coords[0]));
          const mdate = moment(date);
          const x = scale(date);

          const key = mdate.format('YYYY-MM-DD') + 'T00:00:00+00:00';
          const num = capmap[key] ? ((capmap[key].hours || 0) - (capmap[key].scheduled || 0)) : null;

          textcap.attr('x', x - 6)
            .attr('y', box.height - 8)
            .style('fill', num && num < 0 ? d3.interpolateOrRd(0.85) : null)
            .text(num !== null ? num.toFixed(2) + 'h' : null);

          textdate.attr('opacity', 1)
            .attr('x', x + 6)
            .attr('y', box.height - 8)
            .text(mdate.format('MMM DD'));

          marker.attr('x1', x)
            .attr('x2', x)
            .attr('y1', 0)
            .attr('y2', box.height);
        })
        .on('mouseover', (e) => {
          textcap.transition()
            .duration(100)
            .attr('opacity', 1);
          textdate.transition()
            .duration(100)
            .attr('opacity', 1);
          marker.transition()
            .duration(100)
            .attr('stroke-opacity', 1);
        })
        .on('mouseout', (e) => {
          textcap.transition()
            .duration(300)
            .attr('opacity', 0);
          textdate.transition()
            .duration(300)
            .attr('opacity', 0);
          marker.transition()
            .duration(300)
            .attr('stroke-opacity', 0);
        });
      }
      else{
        this.open = null;
      }

      this.calcopen();
    }

    this.loading.next(false);
    this.booted = true;
  }

  get openheight(){
    if(this.open){
      const n = this.body.nativeElement.querySelector('.tml-row[data-id=\''+this.open.id+'\']');
      if(n){
      return n.getBoundingClientRect().height;
      }
    }
    return null;
  }

  private bucketDates(dates: Date[]){
    return dates.reduce((p, c, i, dates) => {
      if(i < dates.length){
        const n = dates[i+1];
        if(c && n) {
          p.push([Globals.toISODate(c), Globals.toISODate(n)]);
        }
      }
      return p;
    }, []);
  }

  private round(date: Date) {
    date.setDate(date.getDate() + Math.round(date.getHours() / 24));
    date.setHours(0, 0, 0, 0);
    return date;
  }

  get f(): Element{
    return this.frame?.nativeElement;
  }

  previewRow(id: number){
    let comp;
    switch (this.tabletype){
      case TableType.Projects:
        comp = ProjectComponent;
        break;
      case TableType.Clients:
        comp = ClientComponent;
        break;
      default:
        comp = UserComponent;
    }
    return this.preview(id, comp);
  }

  preview(id: number, comp = null) {
    if(!comp) {
      switch (this.tabletype) {
        case TableType.Projects:
          comp = UserComponent;
          break;
        case TableType.Clients:
          comp = ProjectComponent;
          break;
        default:
          comp = ProjectComponent;
          break;
      }
    }

    const open = this.dialogService.open<PopupDialogComponent<typeof comp>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      context: {
        comp: comp,
        willclose: (popup) => {
          return this.dirtyDeactivate.warnIfNeeded(<SubscribedComponent>popup.instance);
        },
        componentContext: {
          height: '300px',
          couldEdit: true,
          couldArchive: false,
          couldAssign: false,
          id: id,
          showTimeline: !this.sidebar
        }
      }
    });

    const suba = open.onClose.subscribe(async (res: typeof comp) => {
      suba.unsubscribe();
    });
  }

  private displayFor(cur: any, unit: TimelineType) {
    cur = moment(cur);
    switch (unit){
      case TimelineType.Day:
        return '<span>' + cur.format('MMM') + '</span><b>' + cur.format('D') + '</b>';
      case TimelineType.Week:
        const endof = moment(cur).endOf(unit);
        return this.type === unit ?
          '<b>W' + cur.format('w') + '</b><span>' + cur.format('D') + ' - ' + (endof.month() !== cur.month() ? endof.format('MMM D') : endof.format('D')) + '</span>'
          : '<b>Week ' + cur.format('w') + '</b><span>' + cur.format('MMM D, YYYY') + ' - ' + endof.format('MMM D, YYYY') + '</span>'
      case TimelineType.Month:
        return this.type === unit ? '<b>' + cur.format('MMMM') + '</b>'
          : '<b>' + cur.format('MMMM') + '</b><span>' + cur.format('YYYY') + '</span>';
      case TimelineType.Quarter:
        return this.type === unit ? '<b>Q' + cur.format('Q') + '</b>'
          : '<b>Q' + cur.format('Q') + '</b><span>' + cur.format('YYYY') + '</span>';
      case TimelineType.Year:
        return '<b>' + cur.format('YYYY') + '</b>';
    }
  }

  private calcopen(){
    if(!this.asses || !this.frame?.nativeElement || !this.side?.nativeElement){
      return;
    }

    const left = this.frame.nativeElement.getBoundingClientRect().left;
    const sidewidth = this.side.nativeElement.getBoundingClientRect().width;
    const padding = 5;

    this.asses.select('g')
      .attr('transform', function(d){
        const rect = this.previousSibling.getBoundingClientRect();
        const parentleft = rect.left - left - sidewidth - padding;
        let off = padding;
        if(parentleft < 0){
          const offset = -parentleft + padding;
          if(offset >= rect.width){
            off = rect.width;
          }
          else{
            off = offset;
          }
        }

        return 'translate(' + off + ',0)';
      });
  }

  async scroll($event: Event) {
    if(this.booted) {
      const t = <HTMLElement>($event.target);

      const screen = t.getBoundingClientRect().width;
      const tv = t.getBoundingClientRect().height;
      const cwidth = this.container.nativeElement.getBoundingClientRect().width;
      const cheight = this.container.nativeElement.getBoundingClientRect().height;
      const padding = 25;
      const left = t.scrollLeft;
      const right = left + screen;
      const top = t.scrollTop;
      const bottom = top + tv;
      const working = !this.horizontalappending && this.curdates && !this.isLoading;

      this.calcopen();

      // TODO: If anyone can figure out why 102 and 80 need to be
      //  hard coded below to make this look better, I will buy you lunch
      if(left < padding){
        // append horizontal beginning
        if(working) {
          this.horizontalappending = true;
          this.center = this.curdates[0];
          await this.redraw();

          this.centerVerticalScroll(screen/2 + left - 102);
        }
      }
      else if(right > cwidth - padding){
        // append horizontal end
        if(working) {
          this.horizontalappending = true;
          this.center = this.curdates[this.curdates.length - 1];
          await this.redraw();

          // Why the 80? No idea, just looks better
          this.centerVerticalScroll(-screen/2 + (cwidth + 80 - right));
        }
      }
      else{
        this.horizontalappending = false;
      }

      if(top < padding){
        // append vertical beginning
        if(working && this.page > 1){
          this.verticalappending = true;
          this.page = Math.max( this.page - 0.5, 1);
          await this.redraw();
          this.centerHorizontalScroll(tv/2 + top - 42);
        }
      }
      else if(bottom > cheight - padding && this.curdata && this.curdata.length >= this.size){
        //append vertical end
        if(working){
          this.verticalappending = true;
          this.page += 0.5;
          await this.redraw();
          this.centerHorizontalScroll(-tv/2 + (cheight - bottom));
        }
      }
      else{
        this.verticalappending = false;
      }
    }
  }
}
